import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import API from '@aws-amplify/api';
import { t } from 'i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';

import { userTypes, languageIds } from '../../../utils/enums';
import { ShipyardContext } from '../../Context';
import DateTimeText from '../DateTimeText';
import View from '../View';
import Status from '../Status';
import RenderIf from '../../renderIf';

import './ViewHistory.scss';
import { Box } from '@mui/material';

/**Stylized modal view
 * @param {object} props Incoming JSX attributes.
 * @param {object} props.appId A string value that should be the appId (or possibly other identifier eventually) to retrieve history for.
 * @param {object} props.onClose A function to be called when the close button of the ViewHistory modal is clicked.
 * @returns {JSX.Element} Stylized modal view
 * @see {@link https://bitbucket.org/pcgeducation/shipyard/src/master/shipyard-web/src/components/shared/modals/ViewHistory.md ViewHistory Documentation} for further information
 */

function ColoredTextRenderer(classes, props) {
    return <span className={classes}>{props.children}</span>;
}

function muiColoredText(text, userType) {
    return <Box
        component="span"
        className={['bold', userType].join(' ')}
    >
        {text}
    </Box>;
}

function muiItalicText(text) {
    return <Box
        component='em'
    >
        {text}
    </Box>;
}

export default function ViewHistory(props) {
    const { userSettings } = useContext(ShipyardContext);
    const [initialized, setInitialized] = useState(false);
    const [doingAjax, setDoingAjax] = useState(true);
    const [appId] = useState(props.appId);
    const [historyObjects, setHistoryObjects] = useState([]);
    const [userTypeEnum] = useState(userTypes);
    const [appName, setAppName] = useState('');
    const historyObjectEls = useRef([]);
    const titleEl = useRef();
    const historyObjContainerEl = useRef();

    useEffect(() => {
        if (!initialized) {
            let getHistory = async function () {
                try {
                    //Call API
                    let response = await API.get('ShipyardAPI', '/application/' + appId + '/history');

                    let _appName = response.history.find((obj) => typeof obj.ApplicantName === 'string').ApplicantName;

                    setAppName(_appName);

                    setHistoryObjects(
                        response.history.filter((historyObj) => historyObj.LanguageID === languageIds[userSettings.LanguagePreference])
                    );

                    setInitialized(true);
                    setDoingAjax(false);
                } catch (e) {
                    toast.error(t('General.AjaxError'));
                }
            };
            getHistory();
        }
    }, [initialized, appId, userTypeEnum, userSettings]);

    const isWithdrawal = (object) => {
        return object.QuestionText === 'Withdrawal';
    };

    const isTransfer = (object) => {
        return object.QuestionText === 'School Transfer';
    };
 
    const isNewFee = (object) => { 
        return object.ChgFromDesc === '0' && object.AppChangeColumn === 'IsActive'; 
    };

    const isDeletedFee = (object) => {
        return object?.AppChangeColumn === 'IsActive' && object?.ChgToDesc === '0' && object?.ChgFromDesc === '1';
    };

    const formatDollarAmount = (numberString) => {
        if (numberString.trim() === '') return '';
        if (!numberString.trim().startsWith('$')) {
            return `$${numberString.trim()}`;
        }
        return numberString.trim();
    };

    const historyObjectJSX = useMemo(() => {
        return historyObjects.map((object, i) => {
            let _updateUserType = Object.entries(userTypeEnum).find((ele) => ele[1] === object.UserTypeID);

            switch (object.QueryType) {
                case 'STATUS CHANGE':
                    return (
                        <div
                            key={'history-' + i}
                            className="history-object"
                            ref={(element) => historyObjectEls.current.push(element)}
                        >
                            <span className="timestamp">
                                <span className="grey-dot" />
                                &nbsp;
                                <DateTimeText
                                    type="long-time"
                                    date={object.ChangeDate}
                                />
                            </span>
                            <div className="changes">
                                <ReactMarkdown
                                    disallowedElements={['p']}
                                    unwrapDisallowed={true}
                                    components={{
                                        strong: (renderProps) => ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                    }}
                                >
                                    {t('ViewHistory.StatusLine1', {
                                        ChangeUser: !!object.ActorName?.trim() ? object.ActorName.trim() : 'Unknown User',
                                    })}
                                </ReactMarkdown>{' '}
                                <Status status={object.StatusID} />
                                {object.PrivateSchoolName ? (
                                    <div className="school-container">
                                        {t('ViewHistory.StatusLine2', { SchoolName: object.PrivateSchoolName })}
                                    </div>
                                ) : null}
                                {object.StatusChgDesc ? (
                                    <div className="change-reason-container">
                                        <span className="change-reason-title">{t('ViewHistory.StatusLine3')}</span>
                                        <div className="change-reason">{object.StatusChgDesc}</div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    );
                case 'FILE ADD':
                case 'FILE REMOVE':
                    return (
                        <div
                            key={`history-${i}`}
                            className="history-object"
                            ref={(element) => historyObjectEls.current.push(element)}
                        >
                            <span className="timestamp">
                                <span className="grey-dot" />
                                &nbsp;
                                <DateTimeText
                                    type="long-time"
                                    date={object.ChangeDate}
                                />
                            </span>

                            <div className="changes">
                                <ReactMarkdown
                                    disallowedElements={['p']}
                                    unwrapDisallowed={true}
                                    components={{
                                        strong: (renderProps) => ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                    }}
                                >
                                    {object.QueryType === 'FILE ADD'
                                        ? t('ViewHistory.FileAdded', { ChangeUser: object.ActorName, FileName: [56, 81].includes(parseInt(object.QuestionID)) ? object.AppChangeColumn : object.QuestionText })
                                        : t('ViewHistory.FileRemoved', { ChangeUser: object.ActorName, FileName: [56, 81].includes(object.QuestionID) ? object.AppChangeColumn : object.QuestionText })}
                                </ReactMarkdown>
                            </div>
                        </div>
                    );
                case 'ANNUAL STMT SUBMIT':
                    return (
                        <div
                            key={`history-${i}`}
                            className="history-object"
                            ref={(element) => historyObjectEls.current.push(element)}
                        >
                            <span className="timestamp">
                                <span className="grey-dot" />
                                &nbsp;
                                <DateTimeText
                                    type="long-time"
                                    date={object.ChangeDate}
                                />
                            </span>

                            <div className="changes">
                                <ReactMarkdown
                                    disallowedElements={['p']}
                                    unwrapDisallowed={true}
                                    components={{
                                        strong: (renderProps) => ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                    }}
                                >
                                    {t('ViewHistory.AnnualStatementSubmitted', {
                                        ActorName: object.ActorName,
                                        PrivateSchool: object.PrivateSchoolName?.trim?.(),
                                    })}
                                </ReactMarkdown>
                            </div>
                        </div>
                    );
                case 'ANNUAL STMT':
                    return (
                        <div
                            key={`history-${i}`}
                            className="history-object"
                            ref={(element) => historyObjectEls.current.push(element)}
                        >
                            <span className="timestamp">
                                <span className="grey-dot" />
                                &nbsp;
                                <DateTimeText
                                    type="long-time"
                                    date={object.ChangeDate}
                                />
                            </span>

                            <div className="changes">
                                {/*FeeDeleted*/}
                                <RenderIf isTrue={isDeletedFee(object)}>
                                    {muiColoredText(object.ActorName, _updateUserType[0])} deleted an Annual Statement fee for <i>{object.PrivateSchoolName}</i>.
                                </RenderIf>
                                {/*FeeDeleted*/}

                                {/*FeeCreated*/}
                                <RenderIf isTrue={isNewFee(object)}>
                                    <Box>
                                        {muiColoredText(object.ActorName, _updateUserType[0])} created an Annual Statement fee:  
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(1),
                                            }
                                        }}
                                    >
                                        School: {muiItalicText(object.PrivateSchoolName)}
                                    </Box>
                                </RenderIf>
                                {/*FeeCreated*/}

                                <RenderIf isTrue={object.AppChangeColumn === 'Amount'}>
                                    <Box>
                                    {muiColoredText(object.ActorName, _updateUserType[0])} changed an Annual Statement fee amount:  
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(1),
                                            }
                                        }}
                                    >
                                        School: {muiItalicText(object.PrivateSchoolName)}
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        Previous Amount: {muiItalicText(formatDollarAmount(object.ChgFromDesc))}
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        Current Amount: {muiItalicText(formatDollarAmount(object.ChgToDesc))}
                                    </Box>
                                </RenderIf>

                                <RenderIf isTrue={object.AppChangeColumn === 'Description'}>
                                    <Box>
                                    {muiColoredText(object.ActorName, _updateUserType[0])} changed an Annual Statement fee description:  
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(1),
                                            }
                                        }}
                                    >
                                        School: {muiItalicText(object.PrivateSchoolName)}
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        Previous Description: {muiItalicText(object.ChgFromDesc)}
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        Current Description: {muiItalicText(object.ChgToDesc)}
                                    </Box>
                                </RenderIf>

                                <RenderIf isTrue={object.AppChangeColumn === 'Discount'}>
                                    <Box
                                        sx={theme => {
                                            return {
                                                mb: theme.spacing(1),
                                            }
                                        }}
                                    >
                                        {muiColoredText(object.ActorName, _updateUserType[0])} changed an Annual Statement discount:  
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        Previous Amount: {muiItalicText(formatDollarAmount(object.ChgFromDesc))}
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        Current Amount: {muiItalicText(formatDollarAmount(object.ChgToDesc))}
                                    </Box>
                                </RenderIf>

                                <RenderIf isTrue={object.AppChangeColumn === 'GradeLevelID'}>
                                    <Box
                                        sx={theme => {
                                            return {
                                                mb: theme.spacing(1),
                                            }
                                        }}
                                    >
                                       {muiColoredText(object.ActorName, _updateUserType[0])} changed an Annual Statement grade:  
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        From: {muiItalicText(object.ChgFromDesc)}
                                    </Box>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mt: theme.spacing(0.5),
                                            }
                                        }}
                                    >
                                        To: {muiItalicText(object.ChgToDesc)}
                                    </Box>
                                </RenderIf>
                            </div>
                        </div>
                    );
                case 'ENROLLMENT CHANGE': 
                    return (
                        <Box
                            key={'history-' + i}
                            className="history-object"
                            ref={(element) => historyObjectEls.current.push(element)}
                        >
                            <Box component="span" className="timestamp">
                                <span className="grey-dot" />
                                &nbsp;
                                <DateTimeText
                                    type="long-time"
                                    date={object.ChangeDate}
                                />
                            </Box>

                            <Box
                                className="changes"
                            >
                                <Box
                                    sx={theme=> {
                                        return {
                                            mb: theme.spacing(1),
                                        };
                                    }}
                                >
                                    <ReactMarkdown
                                        disallowedElements={['p']}
                                        unwrapDisallowed
                                        components={{
                                            strong: (renderProps) =>
                                                ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                            em: (renderProps) => 
                                                <Box
                                                    component='span'
                                                    sx={{
                                                        fontWeight: 600,
                                                    }}
                                                    children={renderProps.children}
                                                />,
                                        }}
                                    >
                                        {
                                            t('ViewHistory.ApplicationChangeLine1_1', {
                                                ChangeUser:
                                                    typeof object?.ActorName?.trim === 'function' ? object.ActorName.trim() : 'Unknown User',
                                                Type: object.QuestionText,
                                            })
                                        }
                                    </ReactMarkdown>
                                </Box>
                                
                                <RenderIf isTrue={isTransfer(object)}>
                                    <Box
                                        sx={theme=> {
                                            return {
                                                mb: theme.spacing(1), 
                                            }
                                        }}
                                    >
                                        <ReactMarkdown
                                            disallowedElements={['p']}
                                            unwrapDisallowed={true}
                                            components={{
                                                strong: (renderProps) =>
                                                    ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps)
                                            }}
                                        >
                                            {t('ViewHistory.ApplicationChangeLine3_1', {
                                                PreviousValue: object.ChgFromDesc.trim(),
                                            })}
                                        </ReactMarkdown>
                                        <Box
                                            className="enrollment-date"
                                        >
                                            <ReactMarkdown
                                                disallowedElements={['p']}
                                                unwrapDisallowed={true}
                                            >
                                                {t('ViewHistory.ApplicationChangeLine5')}
                                            </ReactMarkdown>
                                            {object.ExitDate ? (
                                                <Box
                                                    sx={{
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    <DateTimeText
                                                        date={object.ExitDate}
                                                        type="short"
                                                    />
                                                </Box>
                                            ) : (
                                                <Box
                                                    component="span"
                                                    sx={theme=> {
                                                        return {
                                                            ml: theme.spacing(0.25),
                                                            color: theme.palette.submitted.main,
                                                            fontWeight: 500,
                                                        }; 
                                                    }}
                                                >
                                                    N/A
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <ReactMarkdown
                                            disallowedElements={['p']}
                                            unwrapDisallowed={true}
                                            components={{
                                                strong: (renderProps) =>
                                                    ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                            }}
                                        >
                                            {t('ViewHistory.ApplicationChangeLine4_1', {
                                                CurrentValue: object.ChgToDesc.trim(),
                                            })}
                                        </ReactMarkdown>
                                        <Box className="enrollment-date">
                                            <ReactMarkdown
                                                disallowedElements={['p']}
                                                unwrapDisallowed={true}
                                                components={{
                                                    strong: (renderProps) =>
                                                        ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                                }}
                                            >
                                                {t('ViewHistory.ApplicationChangeLine6')}
                                            </ReactMarkdown>
                                            {object.EntryDate ? (
                                                <Box
                                                    sx={{
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    <DateTimeText
                                                        date={object.EntryDate}
                                                        type="short"
                                                    />
                                                </Box>
                                            ) : (
                                                <Box
                                                    component="span"
                                                    sx={theme=> {
                                                        return {
                                                            ml: theme.spacing(0.25),
                                                            fontStyle: 'italic',
                                                        }; 
                                                    }}
                                                >
                                                    N/A
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </RenderIf>

                                <RenderIf isTrue={isWithdrawal(object)}>
                                    <ReactMarkdown
                                        disallowedElements={['p']}
                                        unwrapDisallowed={true}
                                        components={{
                                            strong: (renderProps) =>
                                                ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                        }}
                                    >
                                        {t('ViewHistory.ApplicationChangeLine3_1', {
                                            PreviousValue: object.ChgFromDesc.trim(),
                                        })}
                                    </ReactMarkdown>
                                    <Box className="enrollment-date">
                                        <ReactMarkdown
                                            disallowedElements={['p']}
                                            unwrapDisallowed={true}
                                            components={{
                                                strong: (renderProps) =>
                                                    ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                            }}
                                        >
                                            {t('ViewHistory.ApplicationChangeLine5')}
                                        </ReactMarkdown>

                                        {object.ExitDate ? (
                                            <Box
                                                sx={{
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                <DateTimeText
                                                    date={object.ExitDate}
                                                    type="short"
                                                />
                                            </Box>
                                        ) : (
                                            <Box
                                                component="span"
                                                sx={theme=> {
                                                    return {
                                                        ml: theme.spacing(0.25),
                                                        color: theme.palette.submitted.main,
                                                        fontWeight: 500,
                                                    }; 
                                                }}
                                            >
                                                N/A
                                            </Box>
                                        )}
                                    </Box>
                                </RenderIf>
                            </Box>
                        </Box>
                    );
                default:
                    return (
                        <div
                            key={'history-' + i}
                            className="history-object"
                            ref={(element) => historyObjectEls.current.push(element)}
                        >
                            <span className="timestamp">
                                <span className="grey-dot" />
                                &nbsp;
                                <DateTimeText
                                    type="long-time"
                                    date={object.ChangeDate}
                                />
                            </span>
                            <div className="changes">
                                <ReactMarkdown
                                    disallowedElements={['p']}
                                    unwrapDisallowed={true}
                                    components={{
                                        strong: (renderProps) => ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                    }}
                                >
                                    {
                                        t('ViewHistory.ApplicationChangeLine1', {
                                            ChangeUser:
                                                typeof object?.ActorName?.trim === 'function' ? object.ActorName.trim() : 'Unknown User',
                                            Applicant: object.ApplicantName,
                                            Item: object.QueryType === 'ANNUAL STMT' ? 'statement' : 'application',
                                        })
                                    }
                                </ReactMarkdown>

                                <div className="app-change-2 change-object">
                                    <ReactMarkdown
                                        disallowedElements={['p']}
                                        unwrapDisallowed={true}
                                    >
                                        {t('ViewHistory.ApplicationChangeLine2', {
                                            QuestionName: object.QuestionText,
                                        })}
                                    </ReactMarkdown>
                                </div>

                                <div className="app-change-3">
                                    <ReactMarkdown
                                        disallowedElements={['p']}
                                        unwrapDisallowed={true}
                                    >
                                        {t('ViewHistory.ApplicationChangeLine3', {
                                            PreviousValue: object.ChgFromDesc,
                                        })}
                                    </ReactMarkdown>
                                </div>

                                <div className="app-change-4">
                                    <ReactMarkdown
                                        disallowedElements={['p']}
                                        unwrapDisallowed={true}
                                        components={{
                                            strong: (renderProps) =>
                                                ColoredTextRenderer(['bold', _updateUserType[0]].join(' '), renderProps),
                                        }}
                                    >
                                        {t('ViewHistory.ApplicationChangeLine4', {
                                            CurrentValue:
                                                !!object?.ChgToDesc && typeof object?.ChgToDesc?.trim === 'function'
                                                    ? object.ChgToDesc.trim()
                                                    : 'Field Cleared',
                                        })}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        </div>
                    );
            }
        });
    }, [historyObjects, historyObjectEls, userTypeEnum]);

    const closeClick = () => {
        if (typeof props?.onClose === 'function') props.onClose();
    };

    const viewHistoryTitleJSX = (
        <div ref={titleEl}>
            <h2>
                <ReactMarkdown
                    disallowedElements={['p']}
                    unwrapDisallowed={true}
                    components={{ strong: (renderProps) => ColoredTextRenderer('title-text', renderProps) }}
                >
                    {t('ViewHistory.Title', { ApplicantName: !!appName ? appName : 'No Name' })}
                </ReactMarkdown>{' '}
                <FontAwesomeIcon
                    icon={faTimesCircle}
                    className="close-button"
                    onClick={closeClick}
                />
            </h2>
            <div className="view-history-subtitle">{t('ViewHistory.Subtitle')}</div>
        </div>
    );

    return (
        <View
            viewTitle={viewHistoryTitleJSX}
            doingAjax={doingAjax}
            className="view-history-view"
            name="view-history"
        >
            {historyObjects?.length > 0 ? (
                <div
                    className="history-object-container"
                    ref={historyObjContainerEl}
                >
                    {historyObjectJSX}
                </div>
            ) : (
                <div className="loading">
                    <FontAwesomeIcon icon={faSpinner} />
                </div>
            )}
        </View>
    );
}
